import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";
import api from "../../utils/api";

interface SmileResponse {
  success: boolean;
  message: string;
  data?: {
    smileType: string;
    image: string;
    remainingCredits: number | string;
  };
}

interface SmileState {
  loading: boolean;
  result: SmileResponse | null;
  error: string | null;
  remainingCredits: number | string;
  /** Smile id (or "new") that the in-flight generate belongs to */
  activeRequestKey: string | null;
}

interface GenerateSmilePayload {
  user_id: string;
  file: File;
  customSmile?: string;
  baseSmileId?: string;
  selectiveSmileIds?: number[];
  /** Short display name when using customSmile (avoids dumping full prompt into history) */
  promptLabel?: string;
  /** gpt-image-2 for wireframe, final fix, and any non-engine edit */
  imageModel?: "gpt-image-2";
  /** Ignored. Refine always uses gpt-image-2 at medium quality. */
  refineModel?: "openai";
  /** Current smile_id so a late response cannot land on another case */
  requestKey?: string;
}

interface OverlayFilterPayload {
  user_id: string;
  beforeFile: File;
  filterFile: File;
  customSmile?: string;
  promptLabel?: string;
  requestKey?: string;
}

const initialState: SmileState = {
  loading: false,
  result: null,
  error: null,
  remainingCredits: 0,
  activeRequestKey: null,
};

function isCurrentGenerate(
  state: SmileState,
  requestKey: string | undefined
): boolean {
  return (requestKey ?? "new") === state.activeRequestKey;
}

export const generateSmile = createAsyncThunk<
  SmileResponse,
  GenerateSmilePayload,
  { rejectValue: string }
>(
  "smile/generate",
  async (
    {
      user_id,
      file,
      customSmile,
      baseSmileId,
      selectiveSmileIds,
      promptLabel,
      imageModel,
      refineModel,
    }: GenerateSmilePayload,
    { rejectWithValue }
  ) => {
    try {
      const formData = new FormData();

      formData.append("user_id", user_id);
      formData.append("image", file);

      if (customSmile) {
        formData.append("customSmile", customSmile);
      }

      if (baseSmileId) {
        formData.append("baseSmileId", baseSmileId);
      }

      if (selectiveSmileIds?.length) {
        formData.append(
          "selectiveSmileIds",
          JSON.stringify(selectiveSmileIds)
        );
      }

      if (promptLabel) {
        formData.append("promptLabel", promptLabel);
      }

      if (imageModel) {
        formData.append("imageModel", imageModel);
      }

      if (refineModel) {
        formData.append("refineModel", refineModel);
      }

      const response = await api.post(
        "/smile-design/generate-smile-design",
        formData,
        // Local smile-engine + refine can take several minutes
        { timeout: 600000 }
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
        error?.message ||
        "Smile generation failed"
      );
    }
  }
);

/** Overlay white outline filter onto the before photo via GPT Image 2. */
export const overlayFilterOnBefore = createAsyncThunk<
  SmileResponse,
  OverlayFilterPayload,
  { rejectValue: string }
>(
  "smile/overlayFilterOnBefore",
  async (
    { user_id, beforeFile, filterFile, customSmile, promptLabel },
    { rejectWithValue }
  ) => {
    try {
      const formData = new FormData();
      formData.append("user_id", user_id);
      formData.append("image", beforeFile);
      formData.append("filter", filterFile);
      if (customSmile) formData.append("customSmile", customSmile);
      if (promptLabel) formData.append("promptLabel", promptLabel);

      const response = await api.post(
        "/smile-design/overlay-filter-on-before",
        formData
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Filter overlay failed"
      );
    }
  }
);

const smileDesignSlice = createSlice({
  name: "smile",
  initialState,
  reducers: {
    clearSmileResult: (state) => {
      state.result = null;
    },

    clearSmileError: (state) => {
      state.error = null;
    },

    setRemainingCredits: (
      state,
      action: PayloadAction<number | string>
    ) => {
      state.remainingCredits = action.payload;
    },

    resetSmileState: (state) => {
      state.loading = false;
      state.result = null;
      state.error = null;
      state.remainingCredits = 0;
      state.activeRequestKey = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(generateSmile.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.result = null;
        state.activeRequestKey = action.meta.arg.requestKey ?? "new";
      })

      .addCase(generateSmile.fulfilled, (state, action) => {
        if (!isCurrentGenerate(state, action.meta.arg.requestKey)) return;
        state.loading = false;
        state.result = action.payload;
        state.error = null;
        state.remainingCredits =
          action.payload.data?.remainingCredits ?? state.remainingCredits;
      })

      .addCase(generateSmile.rejected, (state, action) => {
        if (!isCurrentGenerate(state, action.meta.arg.requestKey)) return;
        state.loading = false;
        state.result = null;
        state.error =
          (action.payload as string) ||
          action.error.message ||
          "Smile generation failed";
      })

      .addCase(overlayFilterOnBefore.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.result = null;
        state.activeRequestKey = action.meta.arg.requestKey ?? "new";
      })

      .addCase(overlayFilterOnBefore.fulfilled, (state, action) => {
        if (!isCurrentGenerate(state, action.meta.arg.requestKey)) return;
        state.loading = false;
        state.result = action.payload;
        state.error = null;
        state.remainingCredits =
          action.payload.data?.remainingCredits ?? state.remainingCredits;
      })

      .addCase(overlayFilterOnBefore.rejected, (state, action) => {
        if (!isCurrentGenerate(state, action.meta.arg.requestKey)) return;
        state.loading = false;
        state.result = null;
        state.error =
          (action.payload as string) ||
          action.error.message ||
          "Filter overlay failed";
      });
  },
});

export const {
  clearSmileError,
  clearSmileResult,
  resetSmileState,
  setRemainingCredits,
} = smileDesignSlice.actions;

export default smileDesignSlice.reducer;





// // src/store/smileDesignSlice.ts

// import {
//   createSlice,
//   createAsyncThunk,
//   PayloadAction,
// } from "@reduxjs/toolkit";
// import api from "../../utils/api";

// interface SmileResponse {
//   imageUrl?: string;
//   baseSmileId?: string;
//   selectiveSmileIds?: number[];
//   message?: string;
//   [key: string]: any;
// }

// interface SmileState {
//   loading: boolean;
//   result: SmileResponse | null;
//   error: string | null;
// }

// interface GenerateSmilePayload {
//   user_id: string;
//   file: File;
//   customSmile?: string;
//   baseSmileId?: string;
//   selectiveSmileIds?: number[];
// }

// const initialState: SmileState = {
//   loading: false,
//   result: null,
//   error: null,
// };

// export const generateSmile = createAsyncThunk<
//   SmileResponse,
//   GenerateSmilePayload,
//   { rejectValue: string }
// >(
//   "smile/generate",
//   async (
//     { user_id, file, customSmile, baseSmileId, selectiveSmileIds }: GenerateSmilePayload,
//     { rejectWithValue }
//   ) => {
//     try {
//       const formData = new FormData();

//       formData.append("user_id", user_id);
//       formData.append("image", file);

//       if (customSmile) {
//         formData.append("customSmile", customSmile);
//       }

//       if (baseSmileId) {
//         formData.append("baseSmileId", baseSmileId);
//       }
//       if (selectiveSmileIds?.length) {
//         formData.append(
//           "selectiveSmileIds",
//           JSON.stringify(selectiveSmileIds)
//         );
//       }

//       const response = await api.post(
//         "/smile-design/generate-smile-design",
//         formData
//       );

//       return response.data;
//     } catch (error: any) {
//       return rejectWithValue(
//         error?.response?.data?.message ||
//         error?.message ||
//         "Smile generation failed"
//       );
//     }
//   }
// );

// const smileDesignSlice = createSlice({
//   name: "smile",
//   initialState,
//   reducers: {
//     clearSmileResult: (state) => {
//       state.result = null;
//     },

//     clearSmileError: (state) => {
//       state.error = null;
//     },

//     resetSmileState: (state) => {
//       state.loading = false;
//       state.result = null;
//       state.error = null;
//     },
//   },

//   extraReducers: (builder) => {
//     builder
//       .addCase(generateSmile.pending, (state) => {
//         state.loading = true;
//         state.error = null;
//       })

//       .addCase(
//         generateSmile.fulfilled,
//         (state, action: PayloadAction<SmileResponse>) => {
//           state.loading = false;
//           state.result = action.payload;
//           state.error = null;
//         }
//       )

//       .addCase(generateSmile.rejected, (state, action) => {
//         state.loading = false;
//         state.error =
//           (action.payload as string) ||
//           action.error.message ||
//           "Smile generation failed";
//       });
//   },
// });

// export const {
//   clearSmileError,
//   clearSmileResult,
//   resetSmileState,
// } = smileDesignSlice.actions;

// export default smileDesignSlice.reducer;